import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, ArrowRight, X } from 'lucide-react';

/* ── FloatingCTA ──────────────────────────────────────────────────────────────
   Persistent "Shop Seedlings" pill + first-visit welcome card.
   No emojis — clean, premium, professional.
   ──────────────────────────────────────────────────────────────────────────── */

const HIDE_PATHS = ['/products', '/cart', '/checkout', '/admin'];

const FloatingCTA: React.FC = () => {
  const { pathname } = useLocation();
  const [visible,   setVisible]   = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [welcome,   setWelcome]   = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 3000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('mbegu_welcomed')) {
      const t = setTimeout(() => setWelcome(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  const closeWelcome = () => {
    setWelcome(false);
    localStorage.setItem('mbegu_welcomed', '1');
  };

  if (HIDE_PATHS.some(p => pathname.startsWith(p))) return null;
  if (dismissed && !welcome) return null;

  return (
    <>
      {/* First-visit welcome card — professional, no emojis */}
      {welcome && (
        <div
          className="fixed inset-x-4 top-20 z-[60] max-w-md mx-auto rounded-2xl shadow-2xl overflow-hidden"
          style={{ background: 'linear-gradient(135deg,#0d1f13 0%,#13492F 100%)' }}>
          <button onClick={closeWelcome}
            className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/70"
            aria-label="Close">
            <X className="w-4 h-4" />
          </button>
          <div className="p-6 pr-12">
            <p className="text-white/50 text-[10px] font-bold uppercase tracking-widest mb-2">
              HCD No. 1001934 · Molo, Nakuru County, Kenya
            </p>
            <h3 className="text-white font-bold text-xl leading-tight mb-2">
              Mbegu Commercial Partners
            </h3>
            <p className="text-white/60 text-sm mb-5 leading-relaxed">
              Certified hybrid seedlings delivered to your farm across all 47 counties.
              M-Pesa payment · 90–95% field-verified survival · 25-day delivery guarantee.
            </p>
            <div className="flex gap-2 flex-wrap">
              <Link to="/products" onClick={closeWelcome}
                className="btn btn-white btn-sm gap-1.5">
                <ShoppingCart className="w-3.5 h-3.5" /> View Catalogue
              </Link>
              <Link to="/about" onClick={closeWelcome}
                className="btn btn-ghost btn-sm text-white/60 hover:text-white gap-1">
                About Mbegu <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Persistent bottom-right shop pill */}
      {visible && !dismissed && (
        <div className="fixed bottom-6 right-4 md:right-6 z-50 flex items-center gap-2">
          <button onClick={() => setDismissed(true)}
            className="w-6 h-6 rounded-full bg-white/80 dark:bg-gray-800/80 shadow flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Dismiss">
            <X className="w-3 h-3" />
          </button>
          <Link to="/products"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full shadow-2xl font-semibold text-sm transition-all hover:scale-105"
            style={{ background: 'linear-gradient(135deg,#13492F,#2F9E5E)', color: 'white',
              boxShadow: '0 8px 32px rgba(19,73,47,0.45)' }}>
            <ShoppingCart className="w-4 h-4" />
            <span>Shop Seedlings</span>
            <span className="text-white/55 text-xs font-normal hidden sm:inline">· M-Pesa</span>
          </Link>
        </div>
      )}
    </>
  );
};

export default FloatingCTA;
